import BaseEllipsisMenu from 'components/BaseEllipsisMenu/BaseEllipsisMenu';
import { PermsContextType } from 'contexts/perms.types';
import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { FaEye } from 'react-icons/fa6';
import Popup from 'reactjs-popup';
import { Role } from '../../types/RolesTable.types';

interface Props {
  role: Role;
  perms: PermsContextType;
  yourHighestRoleHierarchyId?: number | null;
  onEdit?: (role: Role) => void;
  onDelete?: (role: Role) => void;
  onView?: (role: Role) => void;
}

export default function RoleActionsMenu({
  role,
  perms,
  yourHighestRoleHierarchyId,
  onEdit,
  onDelete,
  onView,
}: Props) {
  // Controlled open state for EllipsisMenu (fixes TS typing by providing open/onOpen/onClose)
  const [ellipsisOpen, setEllipsisOpen] = useState(false);
  const isSystem = role.role_Id === -1 || role.role_Id === -2;

  if (!perms?.accessLevel) return <div />;

  // Mirror original RolesTable.jsx permission checks: require specific role perms and hierarchy checks
  const roleIdNum = parseInt(String(role.role_Id));
  const roleHierarchyLevel = role.hierarchy_level ?? 0;
  const yourHighest = yourHighestRoleHierarchyId;

  const hasTestPermission =
    typeof perms?.testPermission === 'function'
      ? perms.testPermission([8, 9, 10, 11, 12, 13], ['EditRole']) ||
        perms.testPermission([8, 9, 10, 11, 12, 13], ['DeleteRole'])
      : false;

  const canEditEffective =
    hasTestPermission &&
    roleIdNum >= 0 &&
    (perms.accessLevel === 12 ||
      perms.accessLevel === 13 ||
      roleHierarchyLevel > (yourHighest ?? 999)) &&
    !isSystem;

  const canEditPermission =
    typeof perms?.testPermission === 'function'
      ? perms.testPermission([8, 9, 10, 11, 12, 13], ['EditRole'])
      : perms?.accessLevel >= 12 || perms?.accessLevel === 8;

  const canDeletePermission =
    typeof perms?.testPermission === 'function'
      ? perms.testPermission([8, 9, 10, 11, 12, 13], ['DeleteRole'])
      : false;

  type EllipsisOption = { component?: React.ReactNode };

  const menuOptions: EllipsisOption[] = [];

  if (canEditEffective) {
    if (canEditPermission) {
      menuOptions.push({
        component: (
          <Button
            className="w-100 px-5 text-center cursor-pointer text-white border border-1 border-black"
            onClick={() => onEdit?.(role)}
            variant="secondary"
          >
            Edit
          </Button>
        ),
      });
    }

    if (canDeletePermission) {
      menuOptions.push({
        component: (
          <Button
            className="w-100 px-5 text-center cursor-pointer text-white border border-1 border-black"
            onClick={() => onDelete?.(role)}
            variant="danger"
          >
            Remove
          </Button>
        ),
      });
    }
  }

  if (canEditEffective) {
    return (
      <div>
        <BaseEllipsisMenu
          options={menuOptions}
          open={ellipsisOpen}
          onOpen={() => setEllipsisOpen(true)}
          onClose={() => setEllipsisOpen(false)}
          closeOption={false}
          closeOnScroll={true}
        />
      </div>
    );
  }

  // If user cannot edit, show an eye icon button that opens a small popup to view the role (original behavior)
  return (
    <div>
      <Popup
        trigger={
          <Button
            className="p-0 text-white rounded-circle"
            onClick={() => onView?.(role)}
            variant="secondary"
            style={{
              width: 32,
              height: 32,
            }}
          >
            <FaEye />
          </Button>
        }
        position="top center"
        on="hover"
        closeOnDocumentClick
      >
        <div
          className="d-flex flex-column gap-1 fs-6 fw-normal bg-primary rounded cursor-pointer"
          onClick={() => onView?.(role)}
        >
          <div
            className="d-flex flex-row justify-content-between p-1 px-2"
            style={{ backgroundColor: '#2c373b' }}
          >
            <div>View Role</div>
          </div>
        </div>
      </Popup>
    </div>
  );
}
